/**
 * MONSTER TOWN — NEIGHBOUR REQUESTS. ERRAND CREW.
 *
 * The lighter class of errand, aimed at the build system, from the brief §4:
 * *"could you put a bench by the pond?"*, *"my garden needs three flowers"*.
 * They are ordinary `ErrandDef`s made of `build` steps — no second engine, no
 * second card, no second save section — so a new one is a data drop into
 * `src/content/errands.ts` and nothing else.
 *
 * This file adds the two things a REQUEST needs that a plain errand does not.
 *
 * ===========================================================================
 *  1. CHECKED AGAINST THE REAL TOWN, AND ONLY EVER UPWARDS
 * ===========================================================================
 * `core/errands.ts` adapts `build:placed` for free, so a request already
 * advances when a piece lands. That counts ACTS, not the town. Two gaps:
 *
 *   - a piece that arrived any other way (a load, a grant, a restore) was
 *     never counted, so a kid could be looking at three flowers and be asked
 *     for three flowers;
 *   - nothing knew how many were ALREADY there when the neighbour asked.
 *
 * So this file recounts the actual grid and TOPS UP the difference through the
 * one sanctioned wire (`errand:observe`). It is deliberately a ratchet:
 *
 *   verified = max(0, townCount - baseline)
 *   if (verified > run.have) observe(verified - run.have)
 *
 * It can only ever ADD. Undoing a placement never takes progress away — LAW #2
 * says nothing can be failed, and a three-year-old who tidies up must not be
 * punished for it. What the ratchet buys is that progress is never invented:
 * every dot on the card was, at some moment, a real thing standing in the town.
 *
 * THE BASELINE IS DERIVED, NEVER SAVED. `baseline = townCount - run.have` at
 * the moment a build step is first seen, clamped at zero. That reconstructs
 * itself exactly on a reload from state the architect's save already keeps, so
 * this whole system adds **zero bytes to a kid's town** — SAVES ARE SACRED, and
 * the safest new save section is the one nobody wrote.
 *
 * A kid with twenty flowers who is asked for three is asked for three MORE.
 * That is the following-instructions practice the brief is after; instantly
 * completing it would pay coins and teach nothing.
 *
 * ===========================================================================
 *  2. NEVER ASK FOR SOMETHING A KID CANNOT PLACE
 * ===========================================================================
 * `canAnswer()` asks the wallet whether anything matching the step is in the
 * kid's palette right now. The offer system will not put a request in front of
 * a kid who has not unlocked the pieces for it, so "it gives a stuck kid
 * something to build" can never become "it gives a stuck kid something they
 * cannot build".
 */

import { bus } from '../core/events'
import { wallet } from '../core/economy'
import { errands, getErrand, type BuildStep, type ErrandStep } from '../core/errands'
import { getPlaceable } from '../core/placeable'
import { world } from '../core/scene'
import type { PlaceableId, SceneId } from '../core/types'

/** Does this placeable id satisfy the step? Pure. */
function matchesDef(step: BuildStep, defId: string): boolean {
  if (step.defId !== undefined) return defId === step.defId
  if (step.category !== undefined) {
    return getPlaceable(defId as PlaceableId)?.category === step.category
  }
  return false
}

/** How many matching pieces are ACTUALLY standing in the town right now. */
export function townCount(step: BuildStep): number {
  const scenes: SceneId[] =
    step.sceneId === undefined ? world.ids() : [step.sceneId as SceneId]
  let n = 0
  for (const id of scenes) {
    const node = world.get(id)
    if (node === undefined) continue
    for (const p of node.grid.all()) {
      if (matchesDef(step, String(p.defId))) n++
    }
  }
  return n
}

/** Is there anything in the kid's palette that would answer this step? */
export function canAnswer(step: ErrandStep): boolean {
  if (step.kind !== 'build') return true
  if (step.defId !== undefined) return wallet.canPlace(step.defId as PlaceableId)
  if (step.category === undefined) return false
  return wallet.buildPalette().some((id) => getPlaceable(id)?.category === step.category)
}

/**
 * WHICH PIECES WOULD ANSWER THIS REQUEST, RIGHT NOW.
 *
 * `canAnswer()` says whether any exist; this says WHICH, so the guide arrow can
 * point at one in the piece drawer. Critic, major: *"Build requests guide the
 * kid across town and then abandon them at the piece drawer … nothing on the
 * errand card, nothing in the drawer and nothing from the guide arrow indicates
 * which of the seven picture tabs holds the answer. A 5-year-old might match the
 * card's plant icon to the garden tab's icon; a 3-year-old has to try all
 * seven."*
 *
 * Only pieces the kid can ACTUALLY place: an arrow pointing at a tile with a
 * price badge on it is the same dead end wearing a different hat.
 *
 * Returns `{ id, devName, category }` because `devName` is how a tile is found
 * in the DOM — the build crew labels every tile with it. That is dev-only text
 * used as a selector, never anything shown to a kid (LAW #4).
 */
export function answersFor(
  step: ErrandStep,
): Array<{ id: PlaceableId; devName: string; category: string }> {
  if (step.kind !== 'build') return []
  const out: Array<{ id: PlaceableId; devName: string; category: string }> = []
  for (const id of wallet.buildPalette()) {
    const def = getPlaceable(id)
    if (def === undefined) continue
    if (step.defId !== undefined ? String(id) !== step.defId : def.category !== step.category) {
      continue
    }
    if (!wallet.canPlace(id)) continue
    out.push({ id, devName: def.devName, category: String(def.category) })
  }
  return out
}

/** `errandId#stepIndex` -> how many were already standing when it was asked. */
const baselines = new Map<string, number>()
let reconciling = false

function key(id: string, step: number): string {
  return `${id}#${step}`
}

/**
 * Recount the town and top up every running request. Cheap: the grids are a
 * few dozen placements and this only runs when something actually changed.
 */
export function reconcile(): void {
  if (reconciling) return
  reconciling = true
  try {
    for (const run of errands.active()) {
      const def = getErrand(run.id)
      if (def === undefined) continue
      const step = def.steps[run.step]
      if (step === undefined || step.kind !== 'build') continue
      const have = townCount(step)
      const k = key(run.id, run.step)
      let base = baselines.get(k)
      if (base === undefined) {
        // Derived, not remembered. See the header.
        base = Math.max(0, have - run.have)
        baselines.set(k, base)
      }
      const verified = Math.max(0, have - base)
      if (verified <= run.have) continue
      bus.emit('errand:observe', {
        kind: 'build',
        what: step.defId ?? step.category ?? '',
        count: verified - run.have,
        ...(step.sceneId === undefined ? {} : { sceneId: step.sceneId }),
        ...(step.category === undefined ? {} : { category: step.category }),
      })
    }
  } finally {
    reconciling = false
  }
}

/**
 * Everything a tool needs to prove the ratchet, without reaching into it: what
 * the town really holds versus what the card is showing.
 */
export function requestReport(): Array<{
  id: string
  step: number
  kind: string
  townHave: number
  baseline: number
  cardHave: number
  need: number
}> {
  const out: Array<{
    id: string
    step: number
    kind: string
    townHave: number
    baseline: number
    cardHave: number
    need: number
  }> = []
  for (const run of errands.active()) {
    const def = getErrand(run.id)
    const step = def?.steps[run.step]
    if (def === undefined || step === undefined) continue
    const build = step.kind === 'build' ? step : null
    out.push({
      id: run.id,
      step: run.step,
      kind: step.kind,
      townHave: build === null ? 0 : townCount(build),
      baseline: baselines.get(key(run.id, run.step)) ?? 0,
      cardHave: run.have,
      need: build === null ? 0 : Math.max(1, Math.round(build.count)),
    })
  }
  return out
}

let wired = false

export function installRequests(): void {
  if (wired) return
  wired = true

  // Every way the town's contents can change. `build:placed` is already adapted
  // by core, so on that path this listener normally finds nothing to do — it is
  // the belt to core's braces, and it is what catches everything else.
  bus.on('build:placed', reconcile)
  bus.on('build:removed', reconcile)
  bus.on('build:undo', reconcile)
  bus.on('build:moved', reconcile)
  // Entering a step, taking an errand, or loading a town: recount from scratch.
  bus.on('errand:granted', reconcile)
  bus.on('errand:stepDone', reconcile)
  bus.on('scene:entered', reconcile)
  bus.on('save:loaded', () => {
    baselines.clear()
    reconcile()
  })
}
